(function (FragranceFinder) {
  'use strict';

  const STATE_VERSION = FragranceFinder.STATE_VERSION;
  const EMAIL_RE = FragranceFinder.EMAIL_RE;
  const mobileMediaQuery = FragranceFinder.mobileMediaQuery;
  const parseJson = FragranceFinder.parseJson;
  const normalizeUrl = FragranceFinder.normalizeUrl;
  const pickUrl = FragranceFinder.pickUrl;
  const escapeHtml = FragranceFinder.escapeHtml;
  const getTranslations = FragranceFinder.getTranslations;
  const getSettingText = FragranceFinder.getSettingText;
  const Store = FragranceFinder.Store;
  const klaviyoTrack = FragranceFinder.klaviyoTrack;
  const prefersReducedMotion = FragranceFinder.prefersReducedMotion;
  const normalizeRedirectUrl = FragranceFinder.normalizeRedirectUrl;
  const localeUrl = FragranceFinder.localeUrl;
  const normaliseGender = FragranceFinder.normaliseGender;
  const isPreviewDebugMode = FragranceFinder.isPreviewDebugMode;
  const previewResultsUrl = FragranceFinder.previewResultsUrl;
  const fillPercent = FragranceFinder.fillPercent;
  const firstNotIn = FragranceFinder.firstNotIn;
  const cssEscape = FragranceFinder.cssEscape;

  function parseProductIds(value) {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.map(String).filter(Boolean);
    }
    return String(value).split(',').map(function (id) { return id.trim(); }).filter(Boolean);
  }

  function QuizController(root) {
  this.root = root;
  this.config = parseJson(root.querySelector('[data-ff-config]'), {});
  this.settings = this.config.settings || {};
  this.catalog = this.config.catalog || {};
  this.store = new Store(this.settings.persist !== false);

  this.bgStack = root.querySelector('[data-ff-bg]');
  this._layerMap = {};
  this._layerCurrent = null;
  this._layerPrev = null;
  this._currentBaseUrl = '';
  this._initLayers();
  this._syncLayerSources();

  this.screens = Array.prototype.slice.call(root.querySelectorAll('[data-ff-screen]'));
  this.questionScreens = this.screens.filter(function (screen) {
    return screen.getAttribute('data-ff-screen') === 'question';
  });
  this.questionCount = this.questionScreens.length;
  this.progressTotal = this.questionCount + 1;

  this.liveRegion = root.querySelector('[data-ff-live]');
  this.exit = root.querySelector('[data-ff-exit]');
  this.lastFocused = null;

  this.stepOrder = ['entry'];
  for (let index = 0; index < this.questionCount; index++) this.stepOrder.push('q' + index);
  this.stepOrder.push('owned', 'email');

  this.state = this.loadState();

  this.bindGlobal();
  this.bindEntry();
  this.bindQuestions();
  this.bindOwned();
  this.bindEmail();
  this.bindExit();

  this.setProgressLabels();
  this.goto(this.state.step || 'entry', true);

  document.documentElement.classList.add('ff-quiz-open');

  let controller = this;
  requestAnimationFrame(function () {
    controller.root.classList.add('is-ready');
    const activeScreen = controller.root.querySelector('.ff-screen.is-active');
    const focusTarget = activeScreen && (activeScreen.querySelector('[data-ff-autofocus]') ||
      activeScreen.querySelector('h1, h2, [tabindex="-1"]'));
    if (focusTarget) {
      try { focusTarget.focus({ preventScroll: true }); } catch (error) {}
    }
  });
}

// Keyboard users are kept inside the quiz by the Tab trap wired up in bindGlobal,
// not by marking the rest of the page inert. An earlier version walked up to <body>
// setting inert on every sibling: anything already in the DOM at that moment — the
// cookie banner, the country-redirect modal, the welcome-gift popup — stayed visible
// but stopped receiving clicks for good, and the click fell through to whatever sat
// behind it. Which app lost that race varied per page load, so the page looked
// randomly frozen. Keep background isolation out of this file.

QuizController.prototype.loadState = function () {
  // Answers only need to survive stepping forwards and backwards within one quiz
  // session, and that all happens in memory — the store is purely the hand-off to
  // the results page. So loading the quiz always starts a new run, and whatever a
  // previous run left behind is dropped here rather than resumed.
  this.store.clear();
  return {
    version: STATE_VERSION,
    step: 'entry',
    answers: {},
    owned: [],
    result: null
  };
};

QuizController.prototype.save = function () { this.store.write(this.state); };

QuizController.prototype.announce = function (message) {
  if (this.liveRegion) this.liveRegion.textContent = message;
};

QuizController.prototype._syncLayerSources = function () {
  if (!this.bgStack) return;
  let layers = this.bgStack.querySelectorAll('img.ff-bg-layer');
  Array.prototype.forEach.call(layers, function (element) {
    let url = pickUrl(element.getAttribute('data-bg-desktop'), element.getAttribute('data-bg-mobile'));
    if (url) element.src = url;
  });
};

QuizController.prototype._initLayers = function () {
  let controller = this;
  if (!this.bgStack) return;
  let layers = this.bgStack.querySelectorAll('.ff-bg-layer');
  Array.prototype.forEach.call(layers, function (element) {
    const desktopUrl = normalizeUrl(element.getAttribute('data-bg-desktop') || '');
    const mobileUrl = normalizeUrl(element.getAttribute('data-bg-mobile') || '');
    if (desktopUrl && !controller._layerMap[desktopUrl]) controller._layerMap[desktopUrl] = element;
    if (mobileUrl && !controller._layerMap[mobileUrl]) controller._layerMap[mobileUrl] = element;
  });
};

QuizController.prototype._getLayer = function (url) {
  return (url && this._layerMap[url]) || null;
};

QuizController.prototype._switchLayer = function (url) {
  if (!url) return;
  let layer = this._getLayer(url);

  if (!layer && this.bgStack) {
    layer = document.createElement('img');
    layer.className = 'ff-bg-layer';
    layer.alt = '';
    layer.setAttribute('draggable', 'false');
    layer.setAttribute('aria-hidden', 'true');
    layer.src = url;
    layer.setAttribute('data-bg-desktop', url);
    this.bgStack.appendChild(layer);
    this._layerMap[url] = layer;
  }

  if (!layer || layer === this._layerCurrent) return;

  layer.src = url;

  const previousLayer = this._layerCurrent;
  const priorLayer = this._layerPrev;

  if (priorLayer && priorLayer !== layer && priorLayer !== previousLayer) {
    priorLayer.style.zIndex = '0';
  }

  layer.style.zIndex = '2';
  layer.style.opacity = '1';

  if (previousLayer) {
    previousLayer.style.zIndex = '1';
    previousLayer.style.opacity = '0';
  }

  this._layerPrev = previousLayer;
  this._layerCurrent = layer;
};

QuizController.prototype.setBaseBackground = function (desktopUrl, mobileUrl) {
  let url = pickUrl(desktopUrl, mobileUrl);
  if (!url) return;
  this._currentBaseUrl = url;
  this._switchLayer(url);
};

QuizController.prototype.setHoverBackground = function (desktopUrl, mobileUrl) {
  let url = pickUrl(desktopUrl, mobileUrl);
  if (!url) return;
  this._switchLayer(url);
};

QuizController.prototype.clearHoverBackground = function () {
  if (this._currentBaseUrl) this._switchLayer(this._currentBaseUrl);
};

QuizController.prototype.screenBackground = function (screen) {
  if (!screen) return;
  this.setBaseBackground(screen.getAttribute('data-bg-desktop'),
    screen.getAttribute('data-bg-mobile'));
};

QuizController.prototype._showFirstAnswerBackground = function (screen) {
  let first = screen ? screen.querySelector('[data-answer-id]') : null;
  if (first) {
    this.setBaseBackground(first.getAttribute('data-bg-desktop'),
      first.getAttribute('data-bg-mobile'));
  } else {
    this.screenBackground(screen);
  }
};

QuizController.prototype.refreshBackgroundForStep = function () {
  this._syncLayerSources();
  let step = this.state.step;
  let screen = this.screenForStep(step);
  if (!screen) return;
  if (step.charAt(0) === 'q') {
    let questionIndex = parseInt(step.slice(1), 10);
    let stored = this.state.answers[questionIndex];
    if (stored) {
      let button = screen.querySelector('[data-answer-id="' + cssEscape(stored.answerId) + '"]');
      if (button) {
        this.setBaseBackground(button.getAttribute('data-bg-desktop'),
          button.getAttribute('data-bg-mobile'));
        return;
      }
    }
  }
  this.screenBackground(screen);
};

QuizController.prototype.screenForStep = function (step) {
  if (step && step.charAt(0) === 'q') {
    return this.questionScreens[parseInt(step.slice(1), 10)];
  }
  return this.root.querySelector('[data-ff-screen="' + step + '"]');
};

QuizController.prototype.goto = function (step, skipSave) {
  let target = this.screenForStep(step);
  if (!target) { step = 'entry'; target = this.screenForStep('entry'); }

  this.screens.forEach(function (screen) { screen.classList.remove('is-active'); });
  target.classList.add('is-active');
  this.state.step = step;
  if (!skipSave) this.save();

  if (step.charAt(0) === 'q') {
    let questionIndex = parseInt(step.slice(1), 10);
    let stored = this.state.answers[questionIndex];
    let selectedButton = null;
    if (stored) {
      let button = target.querySelector('[data-answer-id="' + cssEscape(stored.answerId) + '"]');
      if (button) {
        this.setBaseBackground(button.getAttribute('data-bg-desktop'),
          button.getAttribute('data-bg-mobile'));
        if (!mobileMediaQuery.matches) this.markSelected(target, button);
        selectedButton = button;
      } else {
        this._showFirstAnswerBackground(target);
      }
    } else {
      this._showFirstAnswerBackground(target);
    }

    let answerSlider = target.answerSlider;
    if (answerSlider && mobileMediaQuery.matches) {
      const allButtons = target.querySelectorAll('[data-answer-id]');
      const activeButton = selectedButton || allButtons[0] || null;
      const activeIndex = Math.max(0, answerSlider.slides.indexOf(activeButton));
      requestAnimationFrame(function () {
        answerSlider.centerToIndex(activeIndex, false);
        answerSlider.setActiveIndex(activeIndex);
      });
    }
  } else {
    this.screenBackground(target);
  }

  const focusTarget = target.querySelector('[data-ff-autofocus]') ||
    target.querySelector('h1, h2, [tabindex="-1"]');
  if (focusTarget) {
    try { focusTarget.focus({ preventScroll: true }); } catch (error) {}
  }
  this.announce(target.getAttribute('data-announce') || '');
};

QuizController.prototype.stepIndex = function () {
  return this.stepOrder.indexOf(this.state.step);
};

QuizController.prototype.next = function () {
  let index = this.stepIndex();
  if (index < 0) return;
  if (this.state.step === 'email') { this.finish(); return; }
  const nextStep = this.stepOrder[index + 1];
  if (nextStep === 'email' && this.settings.emailStepEnabled === false) {
    this.finish(); return;
  }
  this.goto(nextStep);
};

QuizController.prototype.back = function () {
  let index = this.stepIndex();
  if (index > 0) this.goto(this.stepOrder[index - 1]);
};

QuizController.prototype.setProgressLabels = function () {
  let controller = this;
  this.questionScreens.forEach(function (screen, index) {
    const element = screen.querySelector('[data-ff-progress]');
    if (!element) return;
    const override = screen.getAttribute('data-progress-override');
    element.textContent = override || (index + 1) + ' / ' + controller.progressTotal;
  });
  const ownedProgress = this.root.querySelector('[data-ff-screen="owned"] [data-ff-progress]');
  if (ownedProgress) ownedProgress.textContent = this.progressTotal + ' / ' + this.progressTotal;
};

QuizController.prototype.bindEntry = function () {
  let controller = this;
  const startButton = this.root.querySelector('[data-ff-start]');
  if (startButton) startButton.addEventListener('click', function () {
    const firstStep = controller.questionCount ? 'q0' : 'owned';
    const initial = controller.settings.loadingInitial;
    if (initial) {
      controller.runLoading(initial, prefersReducedMotion() ? 200 : 900, function () {
        controller.goto(firstStep);
      });
    } else {
      controller.goto(firstStep);
    }
  });
};

QuizController.prototype.previewAnswerBackground = function (button) {
  this.setHoverBackground(button.getAttribute('data-bg-desktop'),
    button.getAttribute('data-bg-mobile'));
};

QuizController.prototype.commitAnswerBackground = function (button) {
  this.setBaseBackground(button.getAttribute('data-bg-desktop'),
    button.getAttribute('data-bg-mobile'));
};

QuizController.prototype.markSelected = function (screen, button) {
  let buttons = screen.querySelectorAll('[data-answer-id]');
  Array.prototype.forEach.call(buttons, function (answerButton) {
    let isActive = answerButton === button;
    answerButton.classList.toggle('is-selected', isActive);
    answerButton.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });
};

QuizController.prototype.bindQuestions = function () {
  let controller = this;
  this.questionScreens.forEach(function (screen, questionIndex) {
    const autoAdvance = screen.getAttribute('data-auto-advance') !== 'false';
    let buttons = screen.querySelectorAll('[data-answer-id]');
    let leaveTimer = null;

    function cancelLeave() { clearTimeout(leaveTimer); leaveTimer = null; }
    function scheduleLeave() {
      cancelLeave();
      leaveTimer = setTimeout(function () {
        if (!screen.classList.contains('is-active')) return;
        controller.clearHoverBackground();
        const selectedAnswer = controller.state.answers[questionIndex];
        if (selectedAnswer) {
          let selectedButton = screen.querySelector('[data-answer-id="' + cssEscape(selectedAnswer.answerId) + '"]');
          if (selectedButton) { controller.commitAnswerBackground(selectedButton); return; }
        }
        controller._showFirstAnswerBackground(screen);
      }, 300);
    }

    Array.prototype.forEach.call(buttons, function (button) {
      button.addEventListener('mouseenter', function () {
        if (mobileMediaQuery.matches) return;
        cancelLeave();
        controller.previewAnswerBackground(button);
      });
      button.addEventListener('focus', function () {
        if (!mobileMediaQuery.matches) { cancelLeave(); controller.previewAnswerBackground(button); }
      });
      button.addEventListener('mouseleave', function () {
        if (!mobileMediaQuery.matches) scheduleLeave();
      });
      button.addEventListener('blur', function () {
        if (!mobileMediaQuery.matches) scheduleLeave();
      });

      button.addEventListener('click', function () {
        let answerSlider = screen.answerSlider;
        if (mobileMediaQuery.matches && answerSlider) {
          if (answerSlider.suppressClick) return;
          const slideIndex = answerSlider.slides.indexOf(button);
          if (slideIndex !== answerSlider.index) {
            answerSlider.centerToIndex(slideIndex, true);
            answerSlider.setActiveIndex(slideIndex);
            return;
          }
        }
        controller.selectAnswer(questionIndex, screen, button, autoAdvance);
      });
    });

    let answersElement = screen.querySelector('[data-ff-answers]');
    const dotsContainerElement = document.createElement('div');
    dotsContainerElement.className = 'ff-answer-dots';
    dotsContainerElement.setAttribute('aria-hidden', 'true');
    let dotElements = Array.prototype.map.call(buttons, function (answerButton, index) {
      const dot = document.createElement('span');
      dot.className = 'ff-dot' + (index === 0 ? ' is-active' : '');
      dotsContainerElement.appendChild(dot);
      return dot;
    });
    if (answersElement && answersElement.parentNode) {
      answersElement.parentNode.insertBefore(dotsContainerElement, answersElement.nextSibling);
    }

    screen.answersElement = answersElement;
    screen.dotElements = dotElements;

    if (answersElement) {
      const slides = Array.prototype.slice.call(buttons);
      const track = document.createElement('div');
      track.className = 'ff-answers__track';
      while (answersElement.firstChild) track.appendChild(answersElement.firstChild);
      answersElement.appendChild(track);

      let answerSlider = {
        slides: slides,
        track: track,
        index: 0,
        suppressClick: false,
        centerToIndex: null,
        setActiveIndex: null
      };
      let currentTranslate = 0;
      let isDragging = false;
      let hasMoved = false;
      let startX = 0;
      let startY = 0;
      let startTranslate = 0;
      let dragStartIndex = 0;
      let dragStartTime = 0;

      function getViewportWidth() { return answersElement.clientWidth || window.innerWidth; }
      function getSlideCenter(index) {
        const slide = slides[index];
        return slide.offsetLeft + slide.offsetWidth / 2;
      }
      function getTranslateForIndex(index) { return Math.round(getViewportWidth() / 2 - getSlideCenter(index)); }
      function setTranslate(pixelValue) {
        currentTranslate = pixelValue;
        track.style.transform = 'translateX(' + pixelValue + 'px)';
      }
      function centerToIndex(index, animate) {
        if (!animate) track.style.transition = 'none';
        setTranslate(getTranslateForIndex(index));
        if (!animate) { void track.offsetWidth; track.style.transition = ''; }
        answerSlider.index = index;
      }
      function setActiveIndex(index) {
        answerSlider.index = index;
        slides.forEach(function (slide, slideIndex) {
          let isActive = slideIndex === index;
          slide.classList.toggle('is-selected', isActive);
          if (mobileMediaQuery.matches) slide.setAttribute('aria-pressed', 'false');
        });
        dotElements.forEach(function (dot, dotIndex) { dot.classList.toggle('is-active', dotIndex === index); });
        controller.commitAnswerBackground(slides[index]);
      }
      answerSlider.centerToIndex = centerToIndex;
      answerSlider.setActiveIndex = setActiveIndex;

      answersElement.addEventListener('pointerdown', function (event) {
        if (!mobileMediaQuery.matches) return;
        isDragging = true;
        hasMoved = false;
        startX = event.clientX;
        startY = event.clientY;
        startTranslate = currentTranslate;
        dragStartIndex = answerSlider.index;
        dragStartTime = Date.now();
        track.style.transition = 'none';
        try { answersElement.setPointerCapture(event.pointerId); } catch (error) {}
      });
      answersElement.addEventListener('pointermove', function (event) {
        if (!isDragging) return;
        let deltaX = event.clientX - startX;
        const deltaY = event.clientY - startY;
        if (!hasMoved && Math.abs(deltaY) > Math.abs(deltaX)) {
          isDragging = false;
          track.style.transition = '';
          return;
        }
        if (Math.abs(deltaX) > 4) hasMoved = true;
        const maxTranslate = getTranslateForIndex(0);
        const minTranslate = getTranslateForIndex(slides.length - 1);
        setTranslate(Math.max(minTranslate, Math.min(maxTranslate, startTranslate + deltaX)));
      });
      function endDrag(event) {
        if (!isDragging) return;
        isDragging = false;
        track.style.transition = '';

        let index = dragStartIndex;
        if (hasMoved && event && event.clientX != null) {
          let deltaX = event.clientX - startX;
          const deltaTime = Math.max(1, Date.now() - dragStartTime);
          const threshold = Math.max(28, getViewportWidth() * 0.1);
          const isFlick = Math.abs(deltaX) >= 12 && Math.abs(deltaX / deltaTime) >= 0.18;

          if (deltaX <= -threshold || (isFlick && deltaX < 0)) {
            index = Math.min(slides.length - 1, dragStartIndex + 1);
          } else if (deltaX >= threshold || (isFlick && deltaX > 0)) {
            index = Math.max(0, dragStartIndex - 1);
          }
        }

        centerToIndex(index, true);
        setActiveIndex(index);
        if (hasMoved) {
          answerSlider.suppressClick = true;
          setTimeout(function () { answerSlider.suppressClick = false; }, 60);
        }
      }
      answersElement.addEventListener('pointerup', endDrag);
      answersElement.addEventListener('pointercancel', endDrag);

      dotElements.forEach(function (dot, index) {
        dot.addEventListener('click', function () { centerToIndex(index, true); setActiveIndex(index); });
      });

      window.addEventListener('resize', function () {
        if (mobileMediaQuery.matches) centerToIndex(answerSlider.index, false);
      });

      screen.answerSlider = answerSlider;
    }
  });
};

QuizController.prototype.selectAnswer = function (questionIndex, screen, button, autoAdvance) {
  this.markSelected(screen, button);
  this.commitAnswerBackground(button);
  this.state.answers[questionIndex] = {
    answerId: button.getAttribute('data-answer-id'),
    productIds: parseProductIds(button.getAttribute('data-product-ids')),
    weight: parseFloat(button.getAttribute('data-weight')) || 1,
    label: (button.textContent || '').trim(),
    valueKey: button.getAttribute('data-value-key') || ''
  };
  this.save();
  if (autoAdvance) {
    let controller = this;
    window.setTimeout(function () {
      if (controller.state.step === 'q' + questionIndex) controller.next();
    }, prefersReducedMotion() ? 0 : 420);
  }
};

QuizController.prototype.bindOwned = function () {
  let controller = this;
  let screen = this.root.querySelector('[data-ff-screen="owned"]');
  if (!screen) return;

  let input = screen.querySelector('[data-ff-search-input]');
  const searchWrap = screen.querySelector('.ff-search');
  const results = screen.querySelector('[data-ff-search-results]');
  let nextButton = screen.querySelector('[data-ff-owned-next]');
  let skipButton = screen.querySelector('[data-ff-owned-skip]');

  // The step offers the curated list of fragrances and nothing else, so it is
  // searched here rather than through the store's product search. That search
  // matched on size, SKU and vendor as well as the name, and returned at most
  // ten products *before* they were narrowed to this list — so typing a
  // franchise name could drop members of it. Searching the list directly has no
  // such cap, matches names only, and costs no request.
  const ownedCatalog = (this.config.ownedProducts || []).filter(function (product) {
    return product && product.id && product.title;
  });

  let inputTimer = null;

  // Names carry accents the shopper will not always type: "Rosée" has to be
  // reachable as "rosee".
  function searchable(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function matches(searchTerm) {
    const terms = searchable(searchTerm).split(' ').filter(Boolean);
    if (!terms.length) return [];
    const starts = [];
    const contains = [];
    ownedCatalog.forEach(function (product) {
      const title = searchable(product.title);
      // Every word has to appear, so "delina rosee" narrows rather than widens,
      // while a single "delina" still returns the whole franchise.
      for (let index = 0; index < terms.length; index++) {
        if (title.indexOf(terms[index]) === -1) return;
      }
      if (title.indexOf(terms[0]) === 0) starts.push(product);
      else contains.push(product);
    });
    // Name-first matches lead; within each group the collection's own order is
    // kept, so the merchant decides what a shopper sees first.
    return starts.concat(contains);
  }

  function hideResults() {
    results.hidden = true;
  }

  function render(productList) {
    results.innerHTML = '';
    if (!productList.length) {
      results.hidden = false;
      results.innerHTML = '<li class="ff-search__empty">' +
        escapeHtml(getSettingText(controller.settings, 'ownedNoResults', 'ownedNoResults')) + '</li>';
      return;
    }
    results.hidden = false;
    productList.forEach(function (product) {
      let id = String(product.id);
      const image = product.image || (product.featured_image && product.featured_image.url) || '';
      const listItem = document.createElement('li');
      let button = document.createElement('button');
      button.type = 'button';
      button.className = 'ff-suggestion';
      button.setAttribute('data-product-id', id);
      button.setAttribute('role', 'option');
      let isActive = controller.state.owned.indexOf(id) !== -1;
      if (isActive) button.classList.add('is-selected');
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.innerHTML =
        (image ? '<img class="ff-suggestion__img" src="' + escapeHtml(image) + '" alt="" loading="lazy">' : '') +
        '<span class="ff-suggestion__text">' +
          '<span class="ff-suggestion__title">' + escapeHtml(product.title) + '</span>' +
        '</span>' +
        '<span class="ff-suggestion__check" aria-hidden="true">✓</span>';
      button.addEventListener('click', function () {
        controller.toggleOwned(id, button);
        results.classList.toggle('has-selection', controller.state.owned.length > 0);
      });
      listItem.appendChild(button);
      results.appendChild(listItem);
    });
    results.classList.toggle('has-selection', controller.state.owned.length > 0);
  }

  function onInput() {
    const searchTerm = (input.value || '').trim();
    if (inputTimer) window.clearTimeout(inputTimer);
    if (searchTerm.length < 2) { results.hidden = true; results.innerHTML = ''; return; }
    inputTimer = window.setTimeout(function () { render(matches(searchTerm)); }, 120);
  }

  function goNextStep(event) {
    event.preventDefault();
    event.stopPropagation();
    controller.next();
  }

  function isPointerOnButton(button, event) {
    if (!button || button.hidden) return false;
    if (button.contains(event.target)) return true;
    const rect = button.getBoundingClientRect();
    return event.clientX >= rect.left && event.clientX <= rect.right &&
      event.clientY >= rect.top && event.clientY <= rect.bottom;
  }

  // pointerdown (not click) so this fires before the search results close on
  // outside-mousedown below; click is also bound so keyboard activation
  // (Enter/Space, which never fires pointerdown) still works — the flag stops
  // a mouse click from running goNextStep a second time via its own click.
  function bindActivate(button) {
    if (!button) return;
    let handledByPointer = false;
    button.addEventListener('pointerdown', function (event) {
      handledByPointer = true;
      goNextStep(event);
    });
    button.addEventListener('click', function (event) {
      if (handledByPointer) { handledByPointer = false; return; }
      goNextStep(event);
    });
  }

  if (input) input.addEventListener('input', onInput);
  bindActivate(skipButton);
  bindActivate(nextButton);

  screen.addEventListener('mousedown', function (event) {
    if (isPointerOnButton(nextButton, event) || isPointerOnButton(skipButton, event)) {
      goNextStep(event);
      return;
    }
    if (results.hidden) return;
    if (searchWrap && searchWrap.contains(event.target)) return;
    if (results.contains(event.target)) return;
    hideResults();
  });

  this._syncOwnedNext = function () {
    if (nextButton) nextButton.hidden = controller.state.owned.length === 0;
  };
  this._syncOwnedNext();
};

QuizController.prototype.toggleOwned = function (id, button) {
  id = String(id);
  let index = this.state.owned.indexOf(id);
  if (index === -1) this.state.owned.push(id); else this.state.owned.splice(index, 1);
  let isActive = this.state.owned.indexOf(id) !== -1;
  button.classList.toggle('is-selected', isActive);
  button.setAttribute('aria-selected', isActive ? 'true' : 'false');
  this.save();
  if (this._syncOwnedNext) this._syncOwnedNext();
};

QuizController.prototype.bindEmail = function () {
  let controller = this;
  let screen = this.root.querySelector('[data-ff-screen="email"]');
  if (!screen) return;
  let input = screen.querySelector('[data-ff-email-input]');
  let nextButton = screen.querySelector('[data-ff-email-next]');
  let skipButton = screen.querySelector('[data-ff-email-skip]');
  const error = screen.querySelector('[data-ff-email-error]');

  function sync() {
    if (nextButton) nextButton.hidden = !input || input.value.trim() === '';
  }
  function submit() {
    const value = input ? input.value.trim() : '';
    if (value === '' && controller.settings.emailRequired) {
      showError(getSettingText(controller.settings, 'emailErrorRequired', 'emailErrorRequired'));
      return;
    }
    if (value !== '' && !EMAIL_RE.test(value)) {
      showError(getSettingText(controller.settings, 'emailErrorInvalid', 'emailErrorInvalid'));
      return;
    }
    controller.email = value || '';
    controller.finish();
  }
  function showError(message) {
    if (error) error.textContent = message;
    if (input) input.setAttribute('aria-invalid', 'true');
  }
  function clearError() {
    if (error) error.textContent = '';
    if (input) input.removeAttribute('aria-invalid');
  }

  if (input) {
    input.addEventListener('input', function () { sync(); clearError(); });
    input.addEventListener('keydown', function (event) {
      if (event.key === 'Enter') { event.preventDefault(); submit(); }
    });
  }
  if (nextButton) nextButton.addEventListener('click', submit);
  if (skipButton) skipButton.addEventListener('click', function () {
    if (controller.settings.emailRequired) {
      showError(getSettingText(controller.settings, 'emailErrorRequired', 'emailErrorRequired'));
      return;
    }
    controller.email = '';
    controller.finish();
  });
  sync();
};

QuizController.prototype.finish = function () {
  let result = this.computeResult();
  this.state.result = result;
  this.save();

  let primary = this.catalog[result.primaryId] || {};
  klaviyoTrack(this.settings.klaviyo, this.settings.klaviyo && this.settings.klaviyo.eventName, {
    $email: this.email || undefined,
    recommended_product_title: primary.title || '',
    recommended_product_handle: primary.handle || '',
    recommended_product_id: result.primaryId || '',
    recommendation_ids: result.recommendationIds,
    answers: this.answersForAnalytics(),
    owned_products: this.state.owned,
    scores: result.scores,
    quiz_title: this.settings.quizTitle || '',
    quiz_handle: this.settings.quizHandle || '',
    locale: this.settings.locale || '',
    language_name: this.settings.languageName || '',
    country: this.settings.country || '',
    currency: this.settings.currency || '',
    timestamp: new Date().toISOString()
  });

  let controller = this;
  const delay = prefersReducedMotion() ? 200 : 1600;
  this.runLoading(this.settings.loadingResults, delay, function () {
    controller.redirectToResults();
  }, true);
};

QuizController.prototype.answersForAnalytics = function () {
  const output = [];
  for (let key in this.state.answers) {
    if (!this.state.answers.hasOwnProperty(key)) continue;
    let answer = this.state.answers[key];
    output.push({
      question: parseInt(key, 10) + 1,
      answer: answer.label,
      value: answer.valueKey,
      products: answer.productIds || parseProductIds(answer.productId)
    });
  }
  return output;
};

QuizController.prototype.runLoading = function (label, delay, callback, isDark) {
  let loading = this.root.querySelector('[data-ff-screen="loading"]');
  if (loading) {
    const labelElement = loading.querySelector('[data-ff-loading-label]');
    if (labelElement && label) labelElement.textContent = label;
    loading.classList.toggle('ff-loading--dark', !!isDark);
    this.screens.forEach(function (screen) { screen.classList.remove('is-active'); });
    loading.classList.add('is-active');
    this.screenBackground(loading);
    this.announce(label || getTranslations(this.settings).loading || '');

    const progressBar = loading.querySelector('.ff-loader__bar');
    if (progressBar) {
      progressBar.classList.remove('ff-loader__bar--play');
      void progressBar.offsetWidth;
      progressBar.classList.add('ff-loader__bar--play');
    }
  }
  window.setTimeout(callback, delay);
};

QuizController.prototype.redirectToResults = function () {
  if (isPreviewDebugMode()) { window.location.assign(previewResultsUrl()); return; }
  let url = normalizeRedirectUrl(
    this.settings.resultsUrl,
    localeUrl(this.settings, '/pages/fragrance-finder-results')
  );
  window.location.assign(url);
};

// Fragrances that can stand in for an unfilled recommendation card. Same gender
// as the fragrance being recommended first, because an unrelated gender reads as
// a mistake rather than a surprise; anything else eligible after that, so the
// card is still filled when gender is not set across the catalog.
//
// Ordered by quiz score so a stand-in still reflects the shopper's answers where
// it can, and by product id after that, which keeps a given set of answers
// producing the same result rather than shuffling on a refresh.
QuizController.prototype.surpriseCandidates = function (primaryId, excludedIds, scores) {
  const catalog = this.catalog || {};
  const targetGender = normaliseGender(catalog[primaryId] && catalog[primaryId].gender);
  const sameGender = [];
  const others = [];

  Object.keys(catalog).forEach(function (id) {
    if (excludedIds.indexOf(id) !== -1) return;
    const entry = catalog[id];
    // The catalog is seeded with a placeholder key to keep its JSON valid.
    if (!entry || !entry.title) return;
    if (targetGender && normaliseGender(entry.gender) === targetGender) sameGender.push(id);
    else others.push(id);
  });

  const byScore = function (a, b) { return ((scores && scores[b]) || 0) - ((scores && scores[a]) || 0); };
  sameGender.sort(byScore);
  others.sort(byScore);
  return sameGender.concat(others);
};

QuizController.prototype.computeResult = function () {
  const scores = {};
  const order = [];
  let controller = this;

  function addScore(productId, weight) {
    if (!productId) return;
    if (!(productId in scores)) { scores[productId] = 0; order.push(productId); }
    scores[productId] += weight;
  }

  Object.keys(this.state.answers).sort(function (answerKeyA, answerKeyB) { return answerKeyA - answerKeyB; })
    .forEach(function (questionKey) {
      let answer = controller.state.answers[questionKey];
      if (!answer) return;
      const weight = answer.weight || 1;
      const productIds = answer.productIds || parseProductIds(answer.productId);
      productIds.forEach(function (productId) { addScore(productId, weight); });
    });

  const owned = this.state.owned.map(String);

  let ranked = order.slice().sort(function (productIdA, productIdB) {
    if (scores[productIdB] !== scores[productIdA]) return scores[productIdB] - scores[productIdA];
    return order.indexOf(productIdA) - order.indexOf(productIdB);
  });

  if (!ranked.length) {
    const defaultProductId = this.settings.fallbackPrimaryId;
    ranked = defaultProductId ? [String(defaultProductId)] : [];
  }

  const topScore = ranked.length ? scores[ranked[0]] : 0;
  const tied = ranked.filter(function (id) { return scores[id] === topScore; });
  const isTie = tied.length > 1;

  let primary = ranked[0] || '';
  let related = (this.catalog[primary] && this.catalog[primary].related) || [];

  if (primary && owned.indexOf(primary) !== -1) {
    const promotionPool = related.concat(tied, ranked);
    const promotedProductId = firstNotIn(promotionPool, owned.concat([primary]));
    if (promotedProductId) { primary = promotedProductId; related = (this.catalog[primary] && this.catalog[primary].related) || []; }
  }

  // The two supporting cards come from the primary's
  // fragrance_finder_related_products, in the order the merchant listed them:
  // "You might also like" then "Let us surprise you". Entries that are missing,
  // that repeat the primary, or that the shopper already told us they own are
  // skipped rather than left as a hole, so the cards fill from the left.
  const excluded = owned.concat([String(primary)]);
  const recommendationIds = [];
  related.forEach(function (entry) {
    if (recommendationIds.length >= 2) return;
    const id = entry ? String(entry) : '';
    if (!id || excluded.indexOf(id) !== -1 || recommendationIds.indexOf(id) !== -1) return;
    recommendationIds.push(id);
  });

  // Top the cards up so "Let us surprise you" is never blank — owning one of the
  // related fragrances used to leave it empty, which also left a hole in the
  // Klaviyo payload the recommendation emails are built from.
  if (recommendationIds.length < 2) {
    const fillers = this.surpriseCandidates(primary, excluded.concat(recommendationIds), scores);
    while (recommendationIds.length < 2 && fillers.length) {
      recommendationIds.push(fillers.shift());
    }
  }

  // The results page reads the cards by position, so keep the pair's length even
  // when the catalog had nothing left to offer.
  while (recommendationIds.length < 2) recommendationIds.push('');

  return {
    primaryId: primary,
    recommendationIds: recommendationIds,
    scores: scores,
    tie: isTie,
    ownedIds: owned
  };
};

QuizController.prototype.progressPercent = function () {
  let step = this.state.step;
  const total = this.progressTotal || 1;
  let percent;
  if (step && step.charAt(0) === 'q') {
    percent = Math.round(((parseInt(step.slice(1), 10) + 1) / total) * 100);
  } else if (step === 'entry') {
    percent = 0;
  } else {
    percent = 100;
  }
  return Math.max(0, Math.min(100, percent));
};

QuizController.prototype.bindExit = function () {
  let controller = this;
  const closeButtons = this.root.querySelectorAll('[data-ff-close]');
  Array.prototype.forEach.call(closeButtons, function (button) {
    button.addEventListener('click', function () { controller.openExit(button); });
  });
  if (!this.exit) return;

  this.exitEyebrow = this.exit.querySelector('[data-ff-exit-eyebrow]');
  this.exitEyebrowTemplate = this.exitEyebrow ? this.exitEyebrow.textContent : '';

  const continueButton = this.exit.querySelector('[data-ff-exit-continue]');
  const confirmButton = this.exit.querySelector('[data-ff-exit-confirm]');
  const scrimElement = this.exit.querySelector('[data-ff-exit-scrim]');
  if (continueButton) continueButton.addEventListener('click', function () { controller.closeExit(); });
  if (scrimElement) scrimElement.addEventListener('click', function () { controller.closeExit(); });
  if (confirmButton) confirmButton.addEventListener('click', function () { controller.confirmExit(); });

  this.exit.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') { controller.closeExit(); return; }
    if (event.key === 'Tab') controller.trapFocus(event);
  });

  this.root.addEventListener('keydown', function (event) {
    const exitOpen = controller.exit.classList.contains('is-open');
    if (event.key === 'Tab' && !exitOpen) { controller.trapFocus(event, controller.root); return; }
    if (event.key === 'Escape' && !exitOpen && controller.state.step !== 'entry') {
      controller.openExit(document.activeElement);
    }
  });
};

QuizController.prototype.openExit = function (trigger) {
  if (!this.exit) {
    if (window.console && window.console.warn) {
      window.console.warn('[Fragrance Finder] Exit modal missing — close blocked.');
    }
    return;
  }
  this.lastFocused = trigger || document.activeElement;

  let percent = this.progressPercent();
  this.exit.style.setProperty('--ff-exit-progress', percent + '%');
  if (this.exitEyebrow) {
    this.exitEyebrow.textContent = fillPercent(this.exitEyebrowTemplate, percent);
  }
  this.exit.setAttribute('data-progress', percent);

  this.exit.classList.add('is-open');
  this.exit.removeAttribute('aria-hidden');
  let focusableElements = this.getFocusable(this.exit);
  if (focusableElements.length) try { focusableElements[0].focus(); } catch (error) {}
};

QuizController.prototype.closeExit = function () {
  if (!this.exit) return;
  this.exit.classList.remove('is-open');
  this.exit.setAttribute('aria-hidden', 'true');
  if (this.lastFocused) try { this.lastFocused.focus(); } catch (error) {}
};

QuizController.prototype.confirmExit = function () {
  this.store.clear();
  let url = normalizeRedirectUrl(this.settings.exitRedirectUrl, localeUrl(this.settings, '/'));
  window.location.assign(url);
};

QuizController.prototype.getFocusable = function (container) {
  return Array.prototype.slice.call(container.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )).filter(function (element) {
    // offsetParent alone is not enough: inactive screens and the closed exit modal are
    // hidden with visibility, not display (see .ff-screen / .ff-exit in
    // fragrance-finder-quiz.css), so they keep a layout box and a non-null offsetParent.
    // Without this second test the focus trap borders land on buttons nobody can reach —
    // focus() on them is a silent no-op — so Tab either escapes the quiz or is swallowed.
    if (element.offsetParent === null) return false;
    const visibility = window.getComputedStyle(element).visibility;
    return visibility !== 'hidden' && visibility !== 'collapse';
  });
};

QuizController.prototype.trapFocus = function (event, container) {
  let focusableElements = this.getFocusable(container || this.exit);
  if (!focusableElements.length) return;
  let first = focusableElements[0];
  const last = focusableElements[focusableElements.length - 1];
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
};

QuizController.prototype.bindGlobal = function () {
  let controller = this;

  const backButtons = this.root.querySelectorAll('[data-ff-back]');
  Array.prototype.forEach.call(backButtons, function (backButton) {
    backButton.addEventListener('click', function () { controller.back(); });
  });

  const onChange = function () { controller.refreshBackgroundForStep(); };
  if (mobileMediaQuery.addEventListener) mobileMediaQuery.addEventListener('change', onChange);
  else if (mobileMediaQuery.addListener) mobileMediaQuery.addListener(onChange);
};


  FragranceFinder.QuizController = QuizController;
})(window.FragranceFinder = window.FragranceFinder || {});
